import type { components } from "../api/types";

type Tree = components["schemas"]["Tree"];
type Node = components["schemas"]["Node"];

// Hand-curated demo product used by the onboarding tour. Shape matches
// what GET /api/v1/products/{pid}/tree returns so the read-only preview
// can render with the same conventions as the real product page (without
// actually hitting the API or burning a credit).
//
// The numbers are deliberately "lived in" — 12 questions across 3 topics
// (Pricing, Onboarding, Data integration), 5 confirmed decisions, with
// open/pending split realistic for a product on its 3rd analysis run.
// Topics chosen to span Ring 1 (Who/Why) -> Ring 4 (Features), so the
// rings rail at the side of the demo lights up the right ring as
// "active".

export const DEMO_PRODUCT_ID = "demo";

export const DEMO_TREE: Tree = {
  meta: {
    schema_version: "v1",
    max_depth: 6,
    product_type: "saas",
    idea: "AI-driven CRM for solo consultants",
    moat:
      "The moat is the consultant's own workflow imprint. Every email parsed, every meeting summarised, every nudge accepted or ignored teaches the system how this specific consultant runs deals. After eight weeks of use the next-action recommendations encode tradecraft a horizontal CRM can't replicate by adding an AI sidebar — there's no equivalent corpus to train on.",
    value_mechanism:
      "Replaces 4-6 hours/week of CRM hygiene (logging calls, drafting follow-ups, prepping for the next meeting) with a 10-minute morning review. For a consultant billing $250/hr that's $1,000-$1,500/week of recovered selling time, against a $79/mo seat. The defensible value is the recovered hours, not the AI features themselves.",
    core_experience:
      "Morning ritual: open the app, see three to five cards — \"Send the proposal to Acme\", \"Reply to Lina before noon\", \"Re-engage the Q2 lead that went quiet\". Each card has the draft already written; the consultant approves, edits, or dismisses. The week's pipeline is the byproduct, not the input.",
    differentiation:
      "Horizontal CRMs (HubSpot, Pipedrive) optimise for teams of 5+ with a sales manager watching the pipeline. Solo consultants don't have a manager — they have a backlog. We optimise for the backlog: what to do today, drafted and ready, instead of what to log about yesterday.",
    abandonment_triggers:
      "Founders bounce when the morning cards feel wrong three days in a row — the system confidently suggests the wrong follow-up tone, or misses a deal that obviously needed attention. The first two weeks are the fragile window: if the imprint hasn't compounded by day 14, the consultant goes back to inbox + spreadsheet.",
    ring_rubric: [
      { ring: 0, name: "Comprehension" },
      { ring: 1, name: "Who & Why" },
      { ring: 2, name: "How & What" },
      { ring: 3, name: "Viability" },
      { ring: 4, name: "Features" },
      { ring: 5, name: "User Flows" },
      { ring: 6, name: "Technical" },
    ],
    tree_version: 7,
    latest_run_id: "demo-run-3",
  },
  nodes: buildDemoNodes(),
};

// 1 idea + 12 questions + 5 confirmed decisions = 18 nodes total.
function buildDemoNodes(): Node[] {
  const nodes: Node[] = [];

  nodes.push({
    id: "n_idea",
    type: "idea",
    title: "AI-driven CRM for solo consultants",
    status: "confirmed",
    mvp: "include",
    fields: { ring: 0 },
    refs: [],
  });

  // ---- Pricing topic (4 open questions, 2 confirmed decisions) -------
  const pricing: Question[] = [
    {
      id: "q_price_anchor",
      title: "What price anchor positions us against HubSpot Starter ($20/seat) without racing them to the bottom?",
      ring: 3,
    },
    {
      id: "q_price_trial",
      title: "Should the trial be 14 days (industry default) or 30 days to give the imprint time to compound?",
      ring: 3,
    },
    {
      id: "q_price_annual",
      title: "Annual discount of 20% vs. 2 free months — which framing converts better for solo buyers?",
      ring: 3,
    },
    {
      id: "q_price_addon",
      title: "Is the AI-drafted-email feature included in the base seat or a $20/mo add-on?",
      ring: 3,
    },
  ];
  pricing.forEach((q, i) =>
    nodes.push(
      questionNode(q, i === 0 ? "needs_user" : i === 1 ? "needs_user" : i === 2 ? "assumed_pending" : "assumed_pending"),
    ),
  );

  // ---- Onboarding topic (5 open questions, 2 confirmed decisions) ----
  const onboarding: Question[] = [
    {
      id: "q_onb_import",
      title: "Which inbox source should be wired first — Gmail, Outlook, or a generic IMAP fallback?",
      ring: 2,
    },
    {
      id: "q_onb_calendar",
      title: "Calendar sync: required on day-1 or deferred to the first follow-up loop?",
      ring: 2,
    },
    {
      id: "q_onb_first_card",
      title: "What's the very first card the consultant sees — a draft reply, a re-engagement nudge, or a prep summary?",
      ring: 4,
    },
    {
      id: "q_onb_blank",
      title: "Cold-start with zero email history: do we synthesise demo cards or wait for real data?",
      ring: 4,
    },
    {
      id: "q_onb_consent",
      title: "How do we frame the email-read scope so consent doesn't feel like surveillance?",
      ring: 1,
    },
  ];
  onboarding.forEach((q, i) =>
    nodes.push(questionNode(q, i < 3 ? "needs_user" : "assumed_pending")),
  );

  // ---- Data integration topic (3 open questions, 1 confirmed decision)
  const data: Question[] = [
    {
      id: "q_data_storage",
      title: "Do we store raw email bodies or only LLM-derived summaries to limit blast radius if breached?",
      ring: 6,
    },
    {
      id: "q_data_export",
      title: "Export-on-cancel: full pipeline JSON, vCard, or both?",
      ring: 6,
    },
    {
      id: "q_data_retention",
      title: "Default retention for parsed emails — 90 days, 12 months, or user-configurable?",
      ring: 6,
    },
  ];
  data.forEach((q, i) => nodes.push(questionNode(q, i === 0 ? "needs_user" : "assumed_pending")));

  // ---- 5 confirmed decisions across the same topics ------------------
  const decisions: Decision[] = [
    {
      id: "d_target",
      title: "Target customer is independent consultants billing $150-$400/hr, 1-3 years solo.",
      ring: 1,
    },
    {
      id: "d_no_team",
      title: "v1 ships single-seat only — no team / shared-pipeline UX.",
      ring: 1,
    },
    {
      id: "d_morning",
      title: "The product surface is a single morning ritual, not a kanban board.",
      ring: 4,
    },
    {
      id: "d_drafts",
      title: "AI drafts replies but never auto-sends; consultant approval is required.",
      ring: 4,
    },
    {
      id: "d_imprint",
      title: "Personalisation is per-consultant; no cross-consultant model sharing in v1.",
      ring: 6,
    },
  ];
  decisions.forEach((d) => nodes.push(decisionNode(d, "confirmed")));

  return nodes;
}

type Question = { id: string; title: string; ring: number };
type Decision = Question;

function questionNode(q: Question, status: string): Node {
  return {
    id: q.id,
    type: "question",
    title: q.title,
    status,
    mvp: "include",
    fields: { ring: q.ring },
    refs: [{ to: "n_idea", rel: "scopes" }],
  };
}

function decisionNode(d: Decision, status: string): Node {
  return {
    id: d.id,
    type: "decision",
    title: d.title,
    status,
    mvp: "include",
    fields: { ring: d.ring },
    refs: [{ to: "n_idea", rel: "scopes" }],
  };
}

// Pre-computed counters for the demo header — derived rather than written
// in by hand so they always match the nodes above.
export const DEMO_OPEN_QUESTIONS = DEMO_TREE.nodes.filter(
  (n) => n.type === "question" && (n.status === "needs_user" || n.status === "assumed_pending"),
).length;

export const DEMO_PENDING_DECISIONS = DEMO_TREE.nodes.filter(
  (n) => n.type === "decision" && (n.status === "proposed" || n.status === "assumed_pending"),
).length;

export const DEMO_CONFIRMED_DECISIONS = DEMO_TREE.nodes.filter(
  (n) => n.type === "decision" && n.status === "confirmed",
).length;

export const DEMO_RUN_COUNT = 3;
